import { ethers } from 'ethers';
import { errorLogger } from './errorLogger';
// Token config imports reserved for future use
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getTokenPlaceholderStatus
} from '../config/tokens';

// Minimal ERC20 ABI for balanceOf
const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)'
];

export interface TokenBalance {
  symbol: string;
  balance: string;
  decimals: number;
  error?: BalanceError;
  placeholderWarning?: {
    isPlaceholder: boolean;
    placeholderType?: string;
    warningMessage?: string;
  };
}

export enum BalanceErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  INVALID_TOKEN = 'INVALID_TOKEN',
  RPC_ERROR = 'RPC_ERROR',
  TIMEOUT = 'TIMEOUT',
  INVALID_ADDRESS = 'INVALID_ADDRESS',
  CONTRACT_ERROR = 'CONTRACT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export interface BalanceError {
  type: BalanceErrorType;
  message: string;
  retryable: boolean;
  originalError?: any;
}

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffFactor: 2
};

// Utility function to create delay
const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// Enhanced error handling for balance operations
const handleBalanceError = (error: any, context: string): BalanceError => {
  let errorType: BalanceErrorType;
  let userMessage: string;
  let retryable: boolean;
  
  // Network/RPC errors
  if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('network')) {
    errorType = BalanceErrorType.NETWORK_ERROR;
    userMessage = 'Network connection failed. Please check your internet connection.';
    retryable = true;
  }
  // Timeout errors
  else if (error?.code === 'TIMEOUT' || error?.message?.includes('timeout')) {
    errorType = BalanceErrorType.TIMEOUT;
    userMessage = 'Request timed out. Please try again.';
    retryable = true;
  }
  // Contract/token errors
  else if (error?.code === 'CALL_EXCEPTION' || error?.message?.includes('call exception')) {
    errorType = BalanceErrorType.INVALID_TOKEN;
    userMessage = 'Invalid token contract or token does not exist.';
    retryable = false;
  }
  // Invalid address errors
  else if (error?.message?.includes('invalid address')) {
    errorType = BalanceErrorType.INVALID_ADDRESS;
    userMessage = 'Invalid wallet or token address.';
    retryable = false;
  }
  // RPC errors
  else if (error?.code === -32603 || error?.message?.includes('internal error')) {
    errorType = BalanceErrorType.RPC_ERROR;
    userMessage = 'RPC server error. Please try again in a moment.';
    retryable = true;
  }
  else {
    errorType = BalanceErrorType.UNKNOWN_ERROR;
    userMessage = 'An unexpected error occurred while fetching balance.';
    retryable = true;
  }

  // Log the error
  errorLogger.logBalanceError(
    context.includes('ETH') ? 'ETH' : 'TOKEN',
    error,
    userMessage,
    retryable
  );
  
  return {
    type: errorType,
    message: userMessage,
    retryable,
    originalError: error
  };
};

// Retry wrapper function
async function withRetry<T>(
  operation: () => Promise<T>,
  context: string,
  retryConfig = RETRY_CONFIG
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      const balanceError = handleBalanceError(error, context);
      
      // Don't retry if error is not retryable
      if (!balanceError.retryable) {
        throw error;
      }
      
      // Don't retry if this was the last attempt
      if (attempt === retryConfig.maxRetries) {
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delayMs = Math.min(
        retryConfig.baseDelay * Math.pow(retryConfig.backoffFactor, attempt),
        retryConfig.maxDelay
      );
      
      console.warn(`Retrying ${context} in ${delayMs}ms (attempt ${attempt + 1}/${retryConfig.maxRetries + 1})`);
      await delay(delayMs);
    }
  }
  
  throw lastError;
}

/**
 * Fetches the ETH balance for a given address with retry mechanism
 * @param provider - Ethers provider
 * @param address - Wallet address
 * @returns Formatted ETH balance as string
 */
export async function getEthBalance(provider: ethers.Provider, address: string): Promise<string> {
  // Validate inputs
  if (!provider) {
    throw new Error('Provider is required');
  }
  
  if (!address || !ethers.isAddress(address)) {
    throw new Error('Valid address is required');
  }

  return withRetry(async () => {
    const balance = await provider.getBalance(address);
    return ethers.formatEther(balance);
  }, `ETH balance for ${address}`);
}

/**
 * Fetches the ERC20 token balance for a given address with enhanced error handling
 * @param provider - Ethers provider
 * @param tokenAddress - Token contract address
 * @param address - Wallet address
 * @returns TokenBalance object with symbol, balance, decimals, and optional error
 */
export async function getERC20Balance(
  provider: ethers.Provider,
  tokenAddress: string,
  address: string
): Promise<TokenBalance> {
  // Validate inputs
  if (!provider) {
    const error: BalanceError = {
      type: BalanceErrorType.UNKNOWN_ERROR,
      message: 'Provider is required',
      retryable: false
    };
    return { symbol: 'ERROR', balance: '0', decimals: 18, error };
  }
  
  if (!tokenAddress || !ethers.isAddress(tokenAddress)) {
    const error: BalanceError = {
      type: BalanceErrorType.INVALID_ADDRESS,
      message: 'Valid token address is required',
      retryable: false
    };
    return { symbol: 'ERROR', balance: '0', decimals: 18, error };
  }
  
  if (!address || !ethers.isAddress(address)) {
    const error: BalanceError = {
      type: BalanceErrorType.INVALID_ADDRESS,
      message: 'Valid wallet address is required',
      retryable: false
    };
    return { symbol: 'ERROR', balance: '0', decimals: 18, error };
  }

  try {
    return await withRetry(async () => {
      const contract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
      
      // Create timeout wrapper for contract calls
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Contract call timeout')), 10000);
      });
      
      const contractPromise = Promise.all([
        contract.balanceOf(address),
        contract.decimals(),
        contract.symbol()
      ]);
      
      const [balance, decimals, symbol] = await Promise.race([
        contractPromise,
        timeoutPromise
      ]);

      const formattedBalance = ethers.formatUnits(balance, decimals);
      return {
        symbol,
        balance: formattedBalance,
        decimals
      };
    }, `ERC20 balance for ${tokenAddress}`);
    
  } catch (error) {
    const balanceError = handleBalanceError(error, `ERC20 balance for ${tokenAddress}`);
    return {
      symbol: 'ERROR',
      balance: '0',
      decimals: 18,
      error: balanceError
    };
  }
}

/**
 * Fetches balances for multiple tokens with parallel processing and error isolation
 * @param provider - Ethers provider
 * @param address - Wallet address
 * @param tokens - Array of token addresses (empty string for ETH)
 * @returns Array of TokenBalance objects (errors isolated per token)
 */
export async function getMultipleBalances(
  provider: ethers.Provider,
  address: string,
  tokens: { address: string | null; symbol: string }[]
): Promise<TokenBalance[]> {
  console.log('🔍 [DIAGNOSTIC] getMultipleBalances called:', {
    timestamp: new Date().toISOString(),
    hasProvider: !!provider,
    address,
    tokenCount: tokens?.length || 0,
    tokens: tokens?.map(t => ({ symbol: t.symbol, address: t.address })),
    stackTrace: new Error().stack?.split('\n').slice(1, 3).join('\n')
  });

  // Validate inputs
  if (!provider) {
    throw new Error('Provider is required');
  }
  
  if (!address || !ethers.isAddress(address)) {
    throw new Error('Valid wallet address is required');
  }
  
  if (!tokens || tokens.length === 0) {
    return [];
  }

  // Get network once for all tokens to avoid repeated calls
  let chainId: number;
  try {
    const network = await provider.getNetwork();
    chainId = Number(network.chainId);
    console.log('🌐 [DIAGNOSTIC] Network detected:', chainId);
  } catch (error) {
    console.error('🚨 [DIAGNOSTIC] Failed to get network:', error);
    throw error;
  }

  // Process balances in parallel with error isolation
  const balancePromises = tokens.map(async (token): Promise<TokenBalance> => {
    try {
      console.log(`🔄 [DIAGNOSTIC] Fetching balance for ${token.symbol}:`, {
        address: token.address,
        isNative: !token.address
      });

      // Get placeholder status once per token
      const placeholderStatus = getTokenPlaceholderStatus(token.symbol || 'UNKNOWN', chainId);
      let balanceResult: Omit<TokenBalance, 'placeholderWarning'>;
      
      if (token.address === null || token.address === '' || token.address?.toLowerCase() === '0x0000000000000000000000000000000000000000') {
        // Native token balance (ETH/BNB)
        const nativeBalance = await getEthBalance(provider, address);
        balanceResult = {
          symbol: token.symbol || 'ETH',
          balance: nativeBalance,
          decimals: 18
        };
      } else {
        // ERC20/BEP20 balance
        balanceResult = await getERC20Balance(provider, token.address, address);
      }
      
      // Add placeholder warning if applicable
      const result: TokenBalance = {
        ...balanceResult,
        placeholderWarning: placeholderStatus.isPlaceholder ? placeholderStatus : undefined
      };
      
      // Log placeholder warnings
      if (placeholderStatus.isPlaceholder) {
        console.warn(`🔄 Placeholder Token Detected: ${token.symbol} - ${placeholderStatus.warningMessage}`);
        errorLogger.logError(
          'BALANCE_FETCH' as any,
          'MEDIUM' as any,
          `Placeholder token detected: ${token.symbol}`,
          placeholderStatus.warningMessage || 'Token using placeholder address',
          {
            symbol: token.symbol,
            chainId,
            address: token.address,
            placeholderType: placeholderStatus.placeholderType
          },
          undefined,
          false
        );
      }
      
      return result;
    } catch (error) {
      // Return error state for individual token without failing entire operation
      const balanceError = handleBalanceError(error, `balance for ${token.symbol || token.address || 'native'}`);
      return {
        symbol: token.symbol || 'UNKNOWN',
        balance: '0',
        decimals: 18,
        error: balanceError
      };
    }
  });

  // Wait for all balance fetches to complete
  const results = await Promise.allSettled(balancePromises);
  
  return results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value;
    } else {
      // Handle promise rejection
      const token = tokens[index];
      const balanceError = handleBalanceError(result.reason, `balance for ${token.symbol || token.address}`);
      return {
        symbol: token.symbol || 'ERROR',
        balance: '0',
        decimals: 18,
        error: balanceError
      };
    }
  });
}

/**
 * Validates if a token address exists and is a valid ERC20 contract
 * @param provider - Ethers provider
 * @param tokenAddress - Token contract address to validate
 * @returns Promise<boolean> - true if valid ERC20 token
 */
export async function validateTokenAddress(
  provider: ethers.Provider,
  tokenAddress: string
): Promise<{ isValid: boolean; error?: BalanceError }> {
  try {
    if (!ethers.isAddress(tokenAddress)) {
      return {
        isValid: false,
        error: {
          type: BalanceErrorType.INVALID_ADDRESS,
          message: 'Invalid token address format',
          retryable: false
        }
      };
    }

    return await withRetry(async () => {
      const contract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
      
      // Try to call symbol() function to verify it's an ERC20 token
      await contract.symbol();
      
      return { isValid: true };
    }, `token validation for ${tokenAddress}`);
    
  } catch (error) {
    const balanceError = handleBalanceError(error, `token validation for ${tokenAddress}`);
    return {
      isValid: false,
      error: balanceError
    };
  }
}

export {};